/**
 * The month grid's arithmetic, and the model the grid is drawn from.
 *
 * Pure: no React, no colour, no translated string. Everything the calendar page decides about
 * WHICH days are on screen, WHICH cell an edition band covers and WHAT a week is called is
 * decided here, where it can be asserted without a DOM.
 *
 * ---------------------------------------------------------------------------------------------
 * A DATE HERE IS A CALENDAR DAY, AND NOTHING CONVERTS A ZONE.
 *
 * `calendarDaySchema.date`, `calendarEditionBandSchema.startsOn` and `listTasksQuerySchema`'s
 * `dueFrom`/`dueTo` are all `z.iso.date()` — "2026-10-14" is the fourteenth of October
 * everywhere on earth. `new Date('2026-10-14')` is the opposite of that: the ISO-8601 date form
 * parses as UTC MIDNIGHT, which is 01:00 in Oslo and 16:00 the PREVIOUS DAY in Los Angeles, so a
 * grid built that way puts a deadline in the wrong cell for anybody west of Greenwich.
 *
 * So every `YYYY-MM-DD` in this file is either compared as a STRING — the format sorts
 * lexicographically, which is why `from <= day && day <= to` is a correct range test without a
 * single `Date` — or rebuilt from its components with `parseDateValue`, which produces local
 * midnight. `lib/format.ts`'s `formatDate` is the function deliberately NOT used on the calendar
 * page: it does `new Date(value)` and is right for the timestamps it was written for.
 * `features/festivals/edition-dates.ts` states the same rule for the same reason.
 *
 * WHY THE WINDOW IS THE GRID AND NOT THE MONTH. The request asks for the 42 days the reader can
 * see, leading and trailing days included, so the last days of September carry their real counts
 * in an October grid instead of being drawn blank. Forty-two is inside `CALENDAR_MAX_DAYS` (92)
 * with room to spare.
 *
 * WHY SIX ROWS, ALWAYS. A month spans five or six weeks depending on where its first day falls.
 * A grid that changes height moves the month buttons under the reader's cursor between one month
 * and the next, which is the literal reading of "moving between months must not lose the
 * reader's place". Six rows is also what `DateField`'s picker draws, for the same reason.
 * ---------------------------------------------------------------------------------------------
 */
import type { CalendarDay, CalendarEditionBand, Locale } from '@tvitvi/shared';

import { parseDateValue, toDateValue } from '@/components/ui/DateField';
import { intlTag } from '@/lib/format';

export const WEEK_LENGTH = 7;
/** Six rows always — see the header. */
export const WEEKS_SHOWN = 6;

const MONTH_KEY = /^(\d{4})-(\d{2})$/;

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** Month arithmetic on the first of the month, so it can never roll 31 January into 3 March. */
export function addMonths(month: Date, delta: number): Date {
  return new Date(month.getFullYear(), month.getMonth() + delta, 1);
}

/** `YYYY-MM` — what the address bar carries. */
export function monthKey(month: Date): string {
  return toDateValue(startOfMonth(month)).slice(0, 7);
}

/** The month a `YYYY-MM` names, or null. Rejects `2026-13` rather than rolling it into 2027. */
export function parseMonthKey(value: string): Date | null {
  const match = MONTH_KEY.exec(value.trim());
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return new Date(year, month - 1, 1);
}

/**
 * `Intl`'s own answer, taken from `Intl.Locale`, never hardcoded.
 *
 * `getWeekInfo()` reports `firstDay` in ISO numbering — 1 is Monday, 7 is Sunday — and this
 * returns it in `Date.prototype.getDay()` numbering, where 0 is Sunday. Both locales this
 * product ships answer 1 (`nb-NO` and `en-GB` alike; `en-US` answers 7, which is what makes
 * hardcoding a Monday a latent bug rather than a harmless one).
 *
 * The fallback is Monday and it is not a guess about locales: it is a guess about ENGINES.
 * `getWeekInfo` is recent, an engine without it answers `undefined`, and Monday is what both
 * shipped locales ask for — so the fallback lands on the right answer for this product while
 * the lookup stays the source of truth for the next one.
 */
export function firstWeekdayOf(locale: Locale): number {
  const info = weekInfoOf(intlTag(locale));
  if (info === null) return 1;
  // 7 (Sunday) has to become 0; 1..6 are already `getDay()` numbering.
  return info.firstDay % WEEK_LENGTH;
}

/**
 * Whether this locale's calendars carry week numbers.
 *
 * Norwegian ones do — "uke 42" is how a production office refers to a week out loud — and the
 * numbers on them are ISO-8601 week numbers, which are DEFINED only for a week that starts on
 * Monday. So the column appears exactly where the number it would show is meaningful, and a
 * Sunday-first locale gets a grid with no week column rather than a number computed under a
 * convention its own grid does not follow.
 *
 * `getWeekInfo` used to report `minimalDays` as well, which is the other half of the ISO rule;
 * TC39 removed it, so it cannot be read from `Intl` any more. Deriving the column from
 * `firstDay` is the honest remainder.
 */
export function showsWeekNumbers(locale: Locale): boolean {
  return firstWeekdayOf(locale) === 1;
}

interface WeekInfo {
  firstDay: number;
}

function weekInfoOf(tag: string): WeekInfo | null {
  // `getWeekInfo` is newer than the TypeScript lib this project compiles against, and it shipped
  // as a property before it shipped as a method. Both forms are read; neither is assumed.
  const locale = new Intl.Locale(tag) as Intl.Locale & {
    getWeekInfo?: () => WeekInfo;
    weekInfo?: WeekInfo;
  };
  try {
    const info = typeof locale.getWeekInfo === 'function' ? locale.getWeekInfo() : locale.weekInfo;
    return info !== undefined && typeof info.firstDay === 'number' ? info : null;
  } catch {
    return null;
  }
}

/**
 * The ISO-8601 week number: the week containing the year's first Thursday is week 1.
 *
 * Computed by walking to this week's Thursday and to the first Thursday of that Thursday's year,
 * which is what makes 1 January 2027 come out as week 53 of 2026 rather than as week 1. Local
 * dates throughout — a day is a day (see the header) — and the division is rounded because a
 * daylight-saving change moves two dates an hour closer or further apart, which is a rounding
 * error of 0.6 % of a week.
 */
export function isoWeekNumber(date: Date): number {
  const thursday = thursdayOfWeek(date);
  const firstThursday = thursdayOfWeek(new Date(thursday.getFullYear(), 0, 4));
  const weeksApart = (thursday.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000);
  return 1 + Math.round(weeksApart);
}

function thursdayOfWeek(date: Date): Date {
  // Monday is 0 here, so Thursday is 3 — the ISO week always starts on Monday whatever the
  // locale's grid does, because ISO week NUMBERS are defined that way.
  const mondayIndex = (date.getDay() + 6) % WEEK_LENGTH;
  return addDays(date, 3 - mondayIndex);
}

export interface DayCellModel {
  /** `YYYY-MM-DD`. The identity of the cell, and what a click writes to the address bar. */
  date: string;
  dayOfMonth: number;
  /** False for the leading and trailing days the grid borrows from the neighbouring months. */
  inMonth: boolean;
  isToday: boolean;
  /**
   * `null` when the response carried no entry for this day, which is NOT the same as zero.
   *
   * `calendarResponseSchema.days` holds "one entry per day IN THE WINDOW THAT HAS SOMETHING",
   * so a quiet day is an absence. Drawing it as "0 oppgaver" would put thirty zeroes on a
   * screen whose whole job is to show where the work is.
   */
  counts: { taskCount: number; overdueCount: number } | null;
  /**
   * The årganger running on this day, by name.
   *
   * On the CELL rather than only on the band, because a band drawn across seven cells is not
   * inside any of them — and `role="grid"` ignores everything that is not inside a `gridcell`.
   * Without this the band is a shape a screen reader cannot see at all, and the cell's spoken
   * name is the only place left to say it.
   */
  editionNames: readonly string[];
}

export interface BandSegment {
  editionId: string;
  festivalId: string;
  name: string;
  festivalName: string;
  /** Which horizontal lane inside the week row. Stable across weeks — see `assignLanes`. */
  lane: number;
  /** 1-based column within the week, and how many of the seven columns it covers. */
  column: number;
  span: number;
  /** The årgang began before this week / ends after it. Drawn as a square edge, not a rounded one. */
  continuesBefore: boolean;
  continuesAfter: boolean;
}

export interface WeekModel {
  /** The week's first day, `YYYY-MM-DD`. Stable across a month change, so React keeps the row. */
  key: string;
  weekNumber: number;
  days: readonly DayCellModel[];
  bands: readonly BandSegment[];
}

export interface MonthModel {
  /** The window the request asks for: the first and last day the grid can show, inclusive. */
  from: string;
  to: string;
  weeks: readonly WeekModel[];
  /** How many band lanes the busiest week needs — the height each cell has to reserve. */
  laneCount: number;
}

export interface MonthModelInput {
  month: Date;
  /** `getDay()` numbering, from `firstWeekdayOf`. */
  firstWeekday: number;
  today: Date;
  days: readonly CalendarDay[];
  editions: readonly CalendarEditionBand[];
}

/** The 42 dates a month grid shows, starting on the locale's first weekday on or before the 1st. */
export function windowFor(month: Date, firstWeekday: number): readonly Date[] {
  const first = startOfMonth(month);
  const offset = (first.getDay() - firstWeekday + WEEK_LENGTH) % WEEK_LENGTH;
  const start = addDays(first, -offset);
  return Array.from({ length: WEEKS_SHOWN * WEEK_LENGTH }, (_, index) => addDays(start, index));
}

/**
 * One lane per overlapping årgang, assigned ONCE for the whole month rather than per week.
 *
 * That is the load-bearing part. A band drawn on lane 0 in one week and lane 1 in the next reads
 * as two different things, and the reader's job on this page is to see that one årgang runs
 * across the middle of October. Greedy packing by start date: an årgang takes the first lane
 * whose previous occupant has already ended.
 *
 * Sorted by start, then by the LONGER range first, then by id. The last tiebreak is not
 * decoration — two årganger with identical dates would otherwise swap lanes between renders
 * depending on the order the API happened to return them in.
 */
export function assignLanes(editions: readonly CalendarEditionBand[]): ReadonlyMap<string, number> {
  const ordered = [...editions].sort(
    (a, b) =>
      a.startsOn.localeCompare(b.startsOn) ||
      endOf(b).localeCompare(endOf(a)) ||
      a.id.localeCompare(b.id),
  );

  const lanes: string[] = [];
  const assigned = new Map<string, number>();

  for (const edition of ordered) {
    const end = endOf(edition);
    let lane = lanes.findIndex((occupiedUntil) => occupiedUntil < edition.startsOn);
    if (lane === -1) lane = lanes.length;
    lanes[lane] = end;
    assigned.set(edition.id, lane);
  }

  return assigned;
}

/**
 * An årgang's end, never before its start.
 *
 * `ck_editions__date_order` already forbids the inverse in the database. This is the client
 * refusing to draw a band with a negative span if one ever arrives anyway — a `span: -2` in a
 * CSS grid is not a visual bug, it is a row that stops laying out.
 */
function endOf(edition: CalendarEditionBand): string {
  return edition.endsOn < edition.startsOn ? edition.startsOn : edition.endsOn;
}

export function buildMonthModel({
  month,
  firstWeekday,
  today,
  days,
  editions,
}: MonthModelInput): MonthModel {
  const dates = windowFor(month, firstWeekday);
  const from = toDateValue(dates[0] ?? startOfMonth(month));
  const to = toDateValue(dates[dates.length - 1] ?? startOfMonth(month));

  const counts = new Map(days.map((day) => [day.date, day] as const));
  const todayValue = toDateValue(today);
  const monthIndex = month.getMonth();

  // Defensive: the endpoint returns "every edition whose range overlaps the window", but the
  // grid draws what it was given rather than trusting it — a band outside the window would
  // otherwise claim a lane and push every visible band down a row for no reason on screen.
  const visible = editions.filter((edition) => edition.startsOn <= to && endOf(edition) >= from);
  const lanes = assignLanes(visible);

  const weeks: WeekModel[] = [];
  for (let index = 0; index < WEEKS_SHOWN; index += 1) {
    const week = dates.slice(index * WEEK_LENGTH, index * WEEK_LENGTH + WEEK_LENGTH);
    const first = week[0];
    if (first === undefined) continue;

    weeks.push({
      key: toDateValue(first),
      weekNumber: isoWeekNumber(first),
      days: week.map((date) => {
        const value = toDateValue(date);
        const entry = counts.get(value);
        return {
          date: value,
          dayOfMonth: date.getDate(),
          inMonth: date.getMonth() === monthIndex,
          isToday: value === todayValue,
          counts:
            entry === undefined
              ? null
              : { taskCount: entry.taskCount, overdueCount: entry.overdueCount },
          editionNames: visible
            .filter((edition) => edition.startsOn <= value && endOf(edition) >= value)
            .map((edition) => edition.name),
        };
      }),
      bands: segmentsFor(week, visible, lanes),
    });
  }

  return {
    from,
    to,
    weeks,
    laneCount: lanes.size === 0 ? 0 : Math.max(...lanes.values()) + 1,
  };
}

/**
 * The piece of each band that falls inside one week.
 *
 * `continuesBefore` is what a band that started in September carries into an October grid: the
 * segment is clipped to the week, and the flag says the clip happened so the edge can be drawn
 * square instead of rounded. The endpoint deliberately does NOT clip — "a range clipped to the
 * window would lose the fact that it began before" — and this is the client half of that
 * sentence.
 */
function segmentsFor(
  week: readonly Date[],
  editions: readonly CalendarEditionBand[],
  lanes: ReadonlyMap<string, number>,
): readonly BandSegment[] {
  const values = week.map(toDateValue);
  const weekFrom = values[0];
  const weekTo = values[values.length - 1];
  if (weekFrom === undefined || weekTo === undefined) return [];

  const segments: BandSegment[] = [];
  for (const edition of editions) {
    const start = edition.startsOn;
    const end = endOf(edition);
    if (start > weekTo || end < weekFrom) continue;

    // The first column the band has reached by, and the last it has not passed. Both are found
    // rather than computed from a day difference, so a week that crosses a daylight-saving
    // change cannot shift a band by a column.
    const firstColumn = values.findIndex((value) => value >= start);
    if (firstColumn === -1) continue;
    const lastColumn = values.reduce(
      (found, value, index) => (value <= end ? index : found),
      firstColumn,
    );

    segments.push({
      editionId: edition.id,
      festivalId: edition.festivalId,
      name: edition.name,
      festivalName: edition.festivalName,
      lane: lanes.get(edition.id) ?? 0,
      column: firstColumn + 1,
      span: lastColumn - firstColumn + 1,
      continuesBefore: start < weekFrom,
      continuesAfter: end > weekTo,
    });
  }

  return segments.sort((a, b) => a.lane - b.lane || a.column - b.column);
}

/** Every årgang running on one day, in the order the bands are stacked. */
export function editionsOn(
  date: string,
  editions: readonly CalendarEditionBand[],
): readonly CalendarEditionBand[] {
  const lanes = assignLanes(editions);
  return editions
    .filter((edition) => edition.startsOn <= date && endOf(edition) >= date)
    .sort((a, b) => (lanes.get(a.id) ?? 0) - (lanes.get(b.id) ?? 0));
}

/** Whether a `YYYY-MM-DD` is one of the 42 days a month's grid can draw. */
export function isInWindow(date: string, model: Pick<MonthModel, 'from' | 'to'>): boolean {
  return date >= model.from && date <= model.to;
}

/** The `Date` a `YYYY-MM-DD` names, at local midnight. Re-exported so callers need one import. */
export { parseDateValue, toDateValue };
